import { useNewCode } from '@calendly/feature-check-react'
import { useOnValueChange } from '@calendly/hooks'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

import { FilterTypes } from 'constants/meetings'
import { useGetUserSettingsQuery, usePatchUserSettingsMutation } from 'features/availability'
import { mapChangedFiltersForAnalytics } from 'features/meetings/index'
import { TRACKING_EVENT_NAMES, useTrack } from 'hooks/analytics_events'
import { useFilters } from 'hooks/meetings/filters'

import { BaseFilterModal } from './base_filter_modal'
import { BufferViewCheckbox } from './components/buffer_view_checkbox'
import { DateRange } from './components/date_range'
import { EventTypesSelect } from './components/event_types_select'
import { InviteeEmailSelect } from './components/invitee_email_select'
import { NotetakerRecap } from './components/notetaker_recap'
import { Role } from './components/role'
import { SchedulingMethod } from './components/scheduling_method'
import { Status } from './components/status'
import { TeamsSelect } from './components/teams_select'
import { TrackingSelect } from './components/tracking_select'
import { UsersSelect } from './components/users_select'
import { changedMeetingFilters, hasMeetingsPageSettingsChanged } from './utils'

const AdvancedFilters = ({
  newFilterParams,
  setNewFilterParams,
  isFilterVisible,
  useSaFilterSettingsFlag,
  notetakerEnabled
}) => (
  <>
    {!useSaFilterSettingsFlag && isFilterVisible(FilterTypes.TEAMS) && (
    <TeamsSelect
      params={newFilterParams}
      onApply={setNewFilterParams}
    />
    )}
    {isFilterVisible(FilterTypes.USERS) && (
    <UsersSelect
      params={newFilterParams}
      onApply={setNewFilterParams}
      useColumnLayout={useSaFilterSettingsFlag}
    />
    )}
    {isFilterVisible(FilterTypes.STATUS) && (
      <Status
        params={newFilterParams}
        onChange={setNewFilterParams}
        useColumnLayout={useSaFilterSettingsFlag}
      />
    )}
    {notetakerEnabled && isFilterVisible(FilterTypes.NOTETAKER_RECAP) && (
      <NotetakerRecap
        params={newFilterParams}
        onChange={setNewFilterParams}
        useColumnLayout={useSaFilterSettingsFlag}
      />
    )}
    {!useSaFilterSettingsFlag && isFilterVisible(FilterTypes.SCHEDULING_METHOD) && (
    <SchedulingMethod params={newFilterParams} onChange={setNewFilterParams} />
    )}
    {isFilterVisible(FilterTypes.ROLE) && (
      <Role
        params={newFilterParams}
        onChange={setNewFilterParams}
        useColumnLayout={useSaFilterSettingsFlag}
      />
    )}
    {isFilterVisible(FilterTypes.TRACKING_SELECT) && (
    <TrackingSelect
      params={newFilterParams}
      onApply={setNewFilterParams}
      useColumnLayout={useSaFilterSettingsFlag}
    />
    )}
  </>
)

export const MeetingsFilterModal = ({
  hiddenFilters,
  filterDefaults = {},
  onClose,
  showBuffers: showBuffersProp,
  setShowBuffers: setShowBuffersProp,
  useResultsCount,
  location,
  resetParams,
  filterParams,
  isMeetingsPage,
  isMyCalendlyScope,
  triggerRef,
  notetakerEnabled
}) => {
  const [newFilterParams, setNewFilterParams] = useState(filterParams)
  const isFilterVisible = useCallback((controlType) => !hiddenFilters?.includes?.(controlType), [hiddenFilters])
  const { applyFilterParams, isValidApplyParams } = useFilters()
  const dispatch = useDispatch()
  const track = useTrack()
  const showBufferToggle = isFilterVisible(FilterTypes.BUFFER_VIEW)
  const [showError, setShowError] = useState(false)
  const { data: userSettings } = useGetUserSettingsQuery()
  const [updateUserSettings] = usePatchUserSettingsMutation()
  const showBuffersFromApi = userSettings?.meetingsPageSettings?.showBuffers ?? true
  // Use prop if provided (e.g., from calendar context), otherwise use API value
  const showBuffersSource = showBuffersProp ?? showBuffersFromApi
  const [localShowBuffers, setLocalShowBuffers] = useState(showBuffersSource)
  const { count: resultCount, isLoading: isResultCountLoading } = useResultsCount(newFilterParams, false)
  const changedFilters = changedMeetingFilters(isMeetingsPage, isMyCalendlyScope, newFilterParams)
  const userUuid = userSettings?.userUuid

  useOnValueChange(filterParams.search_keyword, () => setNewFilterParams(filterParams))

  const sendAnalytics = () => {
    const searchType = newFilterParams.search_keyword ? 'text' : 'apply_filter'
    const properties = {
      results_count: resultCount,
      search_type: searchType,
      location: 'meetings_page',
      filters: mapChangedFiltersForAnalytics(changedFilters),
      ...(searchType === 'text' && {
        match_type: 'attendee',
        semantic_search: 'include_related_terms',
        search_text: newFilterParams.search_keyword
      })
    }
    track({
      name: TRACKING_EVENT_NAMES.SEARCH_PERFORMED,
      properties
    })
  }

  useEffect(() => {
    if (!isValidApplyParams(newFilterParams)) {
      setShowError(true)
    } else {
      setShowError(false)
    }
  }, [newFilterParams, isValidApplyParams])

  const onApply = () => {
    if (!showError) {
      sendAnalytics()
      dispatch(applyFilterParams(newFilterParams))

      if (setShowBuffersProp) {
        setShowBuffersProp(localShowBuffers)
      }

      if (isMeetingsPage && userUuid) {
        const { meetingsPageSettings } = userSettings
        const newMeetingsPageSettings = {
          showBuffers: localShowBuffers,
          schedulingMethods: newFilterParams.scheduling_methods || []
        }

        if (hasMeetingsPageSettingsChanged(meetingsPageSettings, newMeetingsPageSettings)) {
          updateUserSettings({
            userUuid,
            user_settings: {
              meetingsPageSettings: newMeetingsPageSettings
            },
            skipToast: true
          })
        }
      }

      onClose()
    }
  }

  const onClear = () => {
    setNewFilterParams({ ...resetParams, ...filterDefaults })
    setLocalShowBuffers(showBuffersSource)
  }

  const useSaFilterSettingsFlag = useNewCode('sa_filter_settings_mtg_pg', false)?.enabled

  return (
    <BaseFilterModal
      isOpen
      onClose={onClose}
      onApply={onApply}
      onClear={onClear}
      hasError={showError}
      advancedFilters={(
        <AdvancedFilters
          newFilterParams={newFilterParams}
          setNewFilterParams={setNewFilterParams}
          isFilterVisible={isFilterVisible}
          useSaFilterSettingsFlag={useSaFilterSettingsFlag}
          notetakerEnabled={notetakerEnabled}
        />
      )}
      isResultsLoading={isResultCountLoading}
      resultCount={resultCount}
      triggerRef={triggerRef}
    >
      {showBufferToggle && !(useSaFilterSettingsFlag && isMeetingsPage) && (
        <BufferViewCheckbox
          showBuffers={localShowBuffers}
          toggleShowBuffers={setLocalShowBuffers}
        />
      )}
      {location !== 'meetings_calendar_page' && (
        <DateRange
          params={newFilterParams}
          onApply={setNewFilterParams}
          useColumnLayout={useSaFilterSettingsFlag}
        />
      )}
      <InviteeEmailSelect params={newFilterParams} onApply={setNewFilterParams} useColumnLayout={useSaFilterSettingsFlag} />
      <EventTypesSelect params={newFilterParams} onApply={setNewFilterParams} useColumnLayout={useSaFilterSettingsFlag} />
    </BaseFilterModal>
  )
}
